import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { PhotographyApiService } from './api.service';
import { CatalogPhotographyVendor } from '../models/catalog-photography-vendor.model';
import { VendorOverrides } from '../models/vendor-overrides.model';
import { ProductOption } from '../models/product-option.model';
import { PhotographyCatalogResponse } from '../models/photography-catalog-response';
import { PhotographyBundlePricingResponse } from '../models/photography-bundle-pricing-response';

type CatalogBundle = PhotographyCatalogResponse['vendors'][number]['bundles'][number];

@Injectable({ providedIn: 'root' })
export class PhotographyCatalogService {
  private readonly photographyApi = inject(PhotographyApiService);

  /**
   * Every vendor with a bundle of the package type for the zip code. lcms-photography does not take releaseId yet
   * and always answers with the current release; the response's releaseId says which one it used.
   */
  lookup$(zipCode: string, packageType: string, releaseId?: string): Observable<PhotographyCatalogResponse> {
    const params = releaseId ? { zipCode, packageType, releaseId } : { zipCode, packageType };
    return this.photographyApi.find$<PhotographyCatalogResponse>('vendors/catalog', { params });
  }

  async getVendors(zipCode: string, packageTitle: string): Promise<CatalogPhotographyVendor[]> {
    const packageType = packageTitle.replace(/\s+package$/i, '').trim().toLowerCase();
    const pricing = await firstValueFrom(this.photographyApi.find$<PhotographyBundlePricingResponse>('bundles/pricing', {
      params: { zipCode },
    }));
    const tier = pricing.items.find((item) => item.packageType?.toLowerCase() === packageType);
    if (!tier?.packageType || tier.cost == null) return [];

    const catalog = await firstValueFrom(this.photographyApi.find$<PhotographyCatalogResponse>('vendors/catalog', {
      params: { zipCode, packageType: tier.packageType },
    }));
    const vendorIds = catalog.vendors.map((vendor) => vendor.vendorId);
    if (!vendorIds.length) return [];

    const baseCost = Number(tier.cost);

    return catalog.vendors.flatMap((vendor) => {
      const bundle = vendor.bundles.filter((item) => item.cost != null)
        .sort((a, b) => Number(a.cost) - Number(b.cost))[0];
      if (!bundle) return [];

      const vendorCost = Number(bundle.cost);
      return [{
        vendorId: vendor.vendorId,
        name: vendor.name,
        email: vendor.email,
        phone: vendor.phone,
        logoImage: vendor.logoImage,
        useCustomScheduling: vendor.useCustomScheduling,
        customSchedulingUrl: vendor.customSchedulingUrl,
        vendorOverrides: new VendorOverrides({
          vendorCost,
          priceAdjustment: Math.round((vendorCost - baseCost) * 100) / 100,
          maxOptionsSelectable: bundle.includes.length + (bundle.maxOptionCount ?? 0),
          modifiedOptions: this.getOptions(bundle),
          catalog: {
            vendorId: vendor.vendorId,
            releaseId: catalog.releaseId,
            bundleId: bundle.bundleId,
            bundlePriceId: bundle.bundlePriceId,
            vendorRegionId: bundle.vendorRegionId,
            baseCost,
            includes: bundle.includes.map((product) => product.name),
          },
        }),
      }];
    });
  }

  private getOptions(bundle: CatalogBundle): ProductOption[] {
    return [
      ...bundle.includes.map((product) => new ProductOption({
        productId: product.productId,
        title: product.name,
        description: product.description ?? product.name,
        assets: { selected: product.selectedImage, unselected: product.unselectedImage },
        selected: true,
        isBundled: true,
        excludeFromCount: false,
        unitPrice: 0,
      })),
      ...bundle.optionals.map((product) => new ProductOption({
        productId: product.productId,
        priceId: product.priceId,
        title: product.name,
        description: product.description ?? product.name,
        assets: { selected: product.selectedImage, unselected: product.unselectedImage },
        selected: false,
        isBundled: false,
        excludeFromCount: false,
        unitPrice: 0,
      })),
      ...bundle.addOns.map((product) => new ProductOption({
        productId: product.productId,
        priceId: product.priceId,
        title: product.name,
        description: product.description ?? product.name,
        assets: { selected: product.selectedImage, unselected: product.unselectedImage },
        selected: false,
        isBundled: false,
        excludeFromCount: true,
        unitPrice: Number(product.cost),
      })),
    ];
  }
}
